import { useQuery } from '@tanstack/react-query'
import { useMemo, useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Calendar,
  Clock,
  Star,
  Trophy,
  Target,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Gift,
  Users,
  TrendingUp,
  BarChart3,
  Sparkles,
  Zap,
  CloudRain,
  Sun,
  Wind,
  Thermometer
} from 'lucide-react'
import { useApi } from '../contexts/ApiContext'
import { useNotificationHelpers } from '../contexts/NotificationContext'
import { formatDate, formatTimeLeft, getEventStatusText, getEventStatusColor, formatEventTime, calculateEventSchedule, getRarityBadgeColor } from '../lib/utils'
import { cn } from '../lib/utils'
import { CurrentEvent } from '../types/api'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
}

const statVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1 }
}

// Event strategies and tips
const eventStrategies = {
  CookingEvent: {
    description: "A culinary event focused on cooking recipes with crops and feeding pigs for rewards",
    tips: [
      "Use the Cooking Pot to craft food with different types of crops",
      "Bigger plants give more rewards but take longer to cook",
      "Stand in the pot while cooking to spin automatically",
      "Focus on Bone Blossom for prismatic recipes",
      "Heavier food gives more points and better rewards",
      "Discover better recipes for improved rewards",
      "Feed the pig with crafted meals for various rewards"
    ],
    optimalWeather: ["sunny", "clear", "rainy"],
    icon: "🍳",
    color: "from-orange-400 to-red-600",
    startDate: "2025-08-02",
    endDate: "2025-08-16",
    duration: "2 weeks"
  },
  ZenAura: {
    description: "A peaceful event focused on meditation and growth",
    tips: [
      "Plant during calm weather for better yields",
      "Focus on rare and epic seeds",
      "Use meditation items for bonus effects",
      "Collect zen crystals for special rewards"
    ],
    optimalWeather: ["sunny", "clear"],
    icon: "🧘",
    color: "from-blue-400 to-purple-600"
  },
  StormChaser: {
    description: "Thriving in chaotic weather conditions",
    tips: [
      "Plant during stormy weather for maximum rewards",
      "Use storm-resistant gear",
      "Focus on lightning-affected crops",
      "Collect storm crystals for bonuses"
    ],
    optimalWeather: ["stormy", "rainy"],
    icon: "⚡",
    color: "from-yellow-400 to-orange-600"
  },
  FrostFestival: {
    description: "Winter wonderland event with frozen treasures",
    tips: [
      "Plant during snowy weather for best results",
      "Use frost-resistant equipment",
      "Focus on ice-type mutations",
      "Collect snowflakes for special items"
    ],
    optimalWeather: ["snowy", "foggy"],
    icon: "❄️",
    color: "from-blue-200 to-cyan-600"
  },
  default: {
    description: "Special event with unique rewards and challenges",
    tips: [
      "Check event-specific requirements",
      "Focus on event-exclusive items",
      "Monitor weather conditions",
      "Participate actively for best rewards"
    ],
    optimalWeather: ["sunny", "clear"],
    icon: "🎉",
    color: "from-purple-400 to-pink-600"
  }
}

// Cooking Event Recipes and Rewards - Complete Database from Wiki
const cookingRecipes = {
  Salad: {
    description: "A fresh and healthy dish perfect for light meals",
    cookingTime: "2-3 minutes",
    difficulty: "Easy",
    rarities: {
      Normal: [
        { name: "1st Option", ingredients: ["2x Tomato"], confirmed: true },
        { name: "2nd Option", ingredients: ["1x Strawberry", "1x Bell Pepper"], confirmed: false },
        { name: "3rd Option", ingredients: ["2x Blood Banana", "2x Tomato"], confirmed: false },
        { name: "4th Option", ingredients: ["1x Onion", "1x Pear"], confirmed: false },
        { name: "5th Option", ingredients: ["2x Tomato", "1x Onion"], confirmed: false },
        { name: "6th Option", ingredients: ["5x Tomato"], confirmed: true },
        { name: "7th Option", ingredients: ["1x Sugar Apple", "1x Bone Blossom", "1x Tomato"], confirmed: true }
      ],
      Legendary: [
        { name: "1st Option", ingredients: ["2x Blood Banana", "2x Tomato"], confirmed: true },
        { name: "2nd Option", ingredients: ["1x Bamboo", "1x Mango", "1x Pineapple", "1x Tomato", "1x Beanstalk"], confirmed: true }
      ],
      Mythical: [
        { name: "1st Option", ingredients: ["1x Tomato", "1x Giant Pinecone"], confirmed: true },
        { name: "2nd Option", ingredients: ["2x Sugar Apple", "1x Pepper", "1x Tomato"], confirmed: true }
      ],
      Divine: [
        { name: "1st Option", ingredients: ["3x Sugar Apple", "1x Pepper", "1x Pineapple"], confirmed: true },
        { name: "2nd Option", ingredients: ["2x Bone Blossom", "1x Pineapple", "1x Pepper", "1x Tomato"], confirmed: true },
        { name: "3rd Option", ingredients: ["4x Elder Strawberry", "1x Onion"], confirmed: false }
      ],
      Prismatic: [
        { name: "1st Option", ingredients: ["1x Tomato", "4x Bone Blossom"], confirmed: true },
        { name: "2nd Option", ingredients: ["1x Bell Pepper", "4x Bone Blossom"], confirmed: false },
        { name: "3rd Option", ingredients: ["5x Grand Tomato"], confirmed: false }
      ]
    }
  },
  Sandwich: {
    description: "A simple but satisfying meal for any time",
    cookingTime: "3-4 minutes",
    difficulty: "Easy",
    rarities: {
      Normal: [
        { name: "1st Option", ingredients: ["2x Tomato", "1x Corn"], confirmed: true }
      ],
      Legendary: [
        { name: "1st Option", ingredients: ["1x Tomato", "1x Corn", "1x Cacao"], confirmed: true },
        { name: "2nd Option", ingredients: ["1x Tomato", "1x Corn", "1x Elder Strawberry"], confirmed: true }
      ],
      Mythical: [
        { name: "1st Option", ingredients: ["2x Giant Pinecone", "1x Tomato", "1x Corn"], confirmed: true },
        { name: "2nd Option", ingredients: ["1x Tomato", "1x Corn", "1x Elder Strawberry", "1x Sugar Apple"], confirmed: false },
        { name: "3rd Option", ingredients: ["1x Tomato", "1x Corn", "1x Elder Strawberry", "1x Sugar Apple", "1x Ember Lily"], confirmed: false }
      ],
      Divine: [
        { name: "1st Option", ingredients: ["3x Bone Blossom", "1x Tomato", "1x Corn"], confirmed: true }
      ],
      Prismatic: [
        { name: "1st Option", ingredients: ["1x Tomato", "4x Elder Strawberries"], confirmed: false }
      ]
    }
  },
  Pie: {
    description: "A comforting baked dish with various fillings",
    cookingTime: "4-5 minutes",
    difficulty: "Medium",
    rarities: {
      Normal: [
        { name: "1st Option", ingredients: ["1x Sugar Apple", "1x Pumpkin", "1x Giant Pinecone", "1x Apple", "1x Corn", "1x Pumpkin"], confirmed: false }
      ],
      Legendary: [
        { name: "1st Option", ingredients: ["1x Pumpkin", "1x Moon Melon"], confirmed: false },
        { name: "2nd Option", ingredients: ["1x Pumpkin", "1x Giant Pinecone", "1x Corn", "1x Apple"], confirmed: false },
        { name: "3rd Option", ingredients: ["1x Pumpkin", "1x Pineapple"], confirmed: false }
      ],
      Mythical: [
        { name: "1st Option", ingredients: ["1x Apple", "1x Pumpkin"], confirmed: true },
        { name: "2nd Option", ingredients: ["1x Sugar Apple", "1x Feijoa", "1x Ember Lily", "1x Coconut", "1x Cauliflower"], confirmed: true },
        { name: "3rd Option", ingredients: ["1x Pumpkin", "1x Beanstalk"], confirmed: false },
        { name: "4th Option", ingredients: ["1x Pumpkin", "1x Ember Lily"], confirmed: false }
      ],
      Divine: [
        { name: "1st Option", ingredients: ["1x Coconut", "1x Beanstalk"], confirmed: true },
        { name: "2nd Option", ingredients: ["1x Coconut", "1x Bone Blossom"], confirmed: false },
        { name: "3rd Option", ingredients: ["1x Pepper", "1x Coconut", "2x Elder Strawberry", "1x Celestiberry"], confirmed: true },
        { name: "4th Option", ingredients: ["3x Sugar Apple", "1x Pumpkin"], confirmed: true }
      ],
      Prismatic: [
        { name: "1st Option", ingredients: ["4x Bone Blossom", "1x Pumpkin"], confirmed: true },
        { name: "2nd Option", ingredients: ["2x Bone Blossom", "1x Coconut"], confirmed: true }
      ]
    }
  },
  Waffle: {
    description: "A crispy breakfast treat with various toppings",
    cookingTime: "5-6 minutes",
    difficulty: "Medium",
    rarities: {
      Normal: [
        { name: "1st Option", ingredients: ["1x Pumpkin", "1x Watermelon"], confirmed: false },
        { name: "2nd Option", ingredients: ["1x Sugar Apple", "1x Watermelon", "1x Corn", "1x Sugarglaze"], confirmed: false },
        { name: "3rd Option", ingredients: ["1x Strawberry", "1x Coconut"], confirmed: false }
      ],
      Mythical: [
        { name: "1st Option", ingredients: ["1x Coconut", "1x Pineapple"], confirmed: true },
        { name: "2nd Option", ingredients: ["1x Sugar Apple", "1x Bone Blossom"], confirmed: false },
        { name: "4th Option", ingredients: ["1x Grape", "1x Coconut", "1x Dragon Fruit", "1x Cactus", "1x Peach"], confirmed: true }
      ],
      Divine: [
        { name: "1st Option", ingredients: ["1x Sugar Apple", "1x Coconut"], confirmed: true }
      ],
      Prismatic: [
        { name: "1st Option", ingredients: ["1x Sugar Apple", "1x Coconut", "3x Bone Blossom"], confirmed: true }
      ]
    }
  },
  HotDog: {
    description: "A classic street food with gourmet ingredients",
    cookingTime: "4-5 minutes",
    difficulty: "Medium",
    rarities: {
      Normal: [
        { name: "1st Option", ingredients: ["1x Pepper", "1x Corn or Banana"], confirmed: false },
        { name: "2nd Option", ingredients: ["2x Corn", "1x Watermelon"], confirmed: false },
        { name: "3rd Option", ingredients: ["1x Pink Lily", "1x Elephant Ears", "1x Bone Blossom", "1x Violet Corn"], confirmed: false },
        { name: "4th Option", ingredients: ["2x Bone Blossom", "1x Pepper", "1x Corn"], confirmed: false },
        { name: "5th Option", ingredients: ["1x or 2x Bone Blossom", "1x Violet Corn"], confirmed: false }
      ],
      Legendary: [
        { name: "1st Option", ingredients: ["1x Pepper", "1x Corn"], confirmed: true }
      ],
      Mythical: [
        { name: "1st Option", ingredients: ["1x Ember Lily", "1x Corn"], confirmed: true },
        { name: "2nd Option", ingredients: ["1x Corn", "1x Bone Blossom"], confirmed: false }
      ],
      Divine: [
        { name: "1st Option", ingredients: ["4x Ember Lily", "1x Corn"], confirmed: true },
        { name: "2nd Option", ingredients: ["2x Bone Blossom", "2x Beanstalk", "1x Corn"], confirmed: true },
        { name: "3rd Option", ingredients: ["3x Bone Blossom", "1x Banana", "1x Blood Banana"], confirmed: true },
        { name: "4th Option", ingredients: ["3x Bone Blossom", "1x Pepper", "1x Tomato"], confirmed: false }
      ],
      Prismatic: [
        { name: "1st Option", ingredients: ["1x Corn", "4x Bone Blossom"], confirmed: true },
        { name: "2nd Option", ingredients: ["1x Violet Corn", "4x Bone Blossom"], confirmed: true }
      ]
    }
  },
  IceCream: {
    description: "A frozen dessert with magical properties",
    cookingTime: "6-7 minutes",
    difficulty: "Hard",
    rarities: {
      Uncommon: [
        { name: "1st Option", ingredients: ["1x Corn", "1x Blueberry"], confirmed: true },
        { name: "2nd Option", ingredients: ["1x Corn", "1x Strawberry"], confirmed: false }
      ],
      Legendary: [
        { name: "1st Option", ingredients: ["2x Banana"], confirmed: true }
      ],
      Mythical: [
        { name: "1st Option", ingredients: ["1x Sugar Apple", "1x Banana"], confirmed: true },
        { name: "2nd Option", ingredients: ["1x Sugar Apple", "1x Corn"], confirmed: true }
      ],
      Divine: [
        { name: "1st Option", ingredients: ["1x Sugar Apple", "1x Sugarglaze"], confirmed: true },
        { name: "2nd Option", ingredients: ["3x Sugar Apple", "1x Corn"], confirmed: true },
        { name: "3rd Option", ingredients: ["2x Sugar Apple", "1x Banana", "1x Tranquil Bloom", "1x Bone Blossom"], confirmed: true }
      ],
      Prismatic: [
        { name: "1st Option", ingredients: ["1x Sugarglaze", "1x Sugar Apple", "3x Bone Blossom"], confirmed: true },
        { name: "2nd Option", ingredients: ["1x Banana", "1x Sugar Apple", "3x Bone Blossom"], confirmed: true }
      ]
    }
  },
  Donut: {
    description: "A sweet ring-shaped pastry with magical properties",
    cookingTime: "5-6 minutes",
    difficulty: "Medium",
    rarities: {
      Normal: [
        { name: "1st Option", ingredients: ["1x Strawberry", "1x Tomato", "1x Apple"], confirmed: false },
        { name: "2nd Option", ingredients: ["2x Corn", "1x Watermelon"], confirmed: false },
        { name: "3rd Option", ingredients: ["2x Corn", "1x Banana", "1x Pumpkin"], confirmed: false }
      ],
      Rare: [
        { name: "1st Option", ingredients: ["1x Corn", "1x Blueberry", "1x Apple"], confirmed: false },
        { name: "2nd Option", ingredients: ["1x Watermelon", "2x Corn"], confirmed: false },
        { name: "3rd Option", ingredients: ["2x Corn", "1x Spiked Mango"], confirmed: false }
      ],
      Legendary: [
        { name: "1st Option", ingredients: ["1x Corn", "1x Serenity", "1x Sugar Apple"], confirmed: false }
      ],
      Mythical: [
        { name: "1st Option", ingredients: ["1x Sugar Apple", "2x Corn"], confirmed: true }
      ],
      Divine: [
        { name: "1st Option", ingredients: ["1x Bone Blossom", "1x Sugar Apple", "1x Banana"], confirmed: false }
      ],
      Prismatic: [
        { name: "1st Option", ingredients: ["1x Sugarglaze", "4x Bone Blossom"], confirmed: true }
      ]
    }
  },
  Pizza: {
    description: "A divine dish with heavenly flavors and properties",
    cookingTime: "7-8 minutes",
    difficulty: "Hard",
    rarities: {
      Normal: [
        { name: "1st Option", ingredients: ["1x Banana", "1x Tomato"], confirmed: false },
        { name: "2nd Option", ingredients: ["1x Ember Lily", "1x Corn", "1x Tomato"], confirmed: false },
        { name: "3rd Option", ingredients: ["1x Giant Pinecone", "1x Corn", "1x Apple", "1x Pepper", "1x Strawberry"], confirmed: false },
        { name: "4th Option", ingredients: ["1x Strawberry", "1x Pepper", "1x Corn", "1x Tomato"], confirmed: false }
      ],
      Legendary: [
        { name: "1st Option", ingredients: ["1x Tomato", "1x Banana", "2x Corn"], confirmed: false },
        { name: "2nd Option", ingredients: ["2x Corn", "2x Apple", "1x Pepper"], confirmed: false },
        { name: "3rd Option", ingredients: ["1x Bell Pepper", "1x Pepper", "1x Tomato", "1x Corn", "1x Peach"], confirmed: false }
      ],
      Mythical: [
        { name: "1st Option", ingredients: ["1x Pepper", "1x Tomato", "1x Corn", "2x Sugar Apple"], confirmed: true },
        { name: "2nd Option", ingredients: ["1x Giant Pinecone", "1x Apple", "1x Pepper", "1x Banana", "1x Mushroom"], confirmed: true }
      ],
      Divine: [
        { name: "1st Option", ingredients: ["1x Sugar Apple", "1x Pepper", "1x Banana"], confirmed: true },
        { name: "2nd Option", ingredients: ["1x Sugar Apple", "1x Corn", "3x Bone Blossom"], confirmed: true }
      ],
      Prismatic: [
        { name: "1st Option", ingredients: ["1x Violet Corn", "1x Sugar Apple", "3x Bone Blossom"], confirmed: true }
      ]
    }
  },
  Sushi: {
    description: "An elegant dish with divine properties and presentation",
    cookingTime: "8-9 minutes",
    difficulty: "Expert",
    rarities: {
      Normal: [
        { name: "1st Option", ingredients: ["4x Bamboo", "1x Corn"], confirmed: true }
      ],
      Legendary: [
        { name: "1st Option", ingredients: ["3x Bamboo", "1x Corn", "1x Maple Apple"], confirmed: false },
        { name: "2nd Option", ingredients: ["3x Bamboo", "1x Hive Fruit", "1x Corn"], confirmed: false },
        { name: "3rd Option", ingredients: ["1x Bamboo", "1x Lilac", "1x Lucky Bamboo", "1x Mango", "1x Violet Corn"], confirmed: true }
      ],
      Mythical: [
        { name: "1st Option", ingredients: ["2x Bamboo", "1x Corn", "2x Bone Blossom"], confirmed: true },
        { name: "2nd Option", ingredients: ["3x Sugar Apple", "1x Bamboo", "1x Corn"], confirmed: true }
      ],
      Divine: [
        { name: "1st Option", ingredients: ["1x Bamboo", "1x Corn", "3x Bone Blossom"], confirmed: true }
      ],
      Prismatic: [
        // TBA - no recipes yet
      ]
    }
  },
  Cake: {
    description: "The ultimate dessert with prismatic properties and effects",
    cookingTime: "10-12 minutes",
    difficulty: "Master",
    rarities: {
      Normal: [
        { name: "1st Option", ingredients: ["2x Corn", "2x Strawberry"], confirmed: false },
        { name: "2nd Option", ingredients: ["2x Blueberry", "1x Corn", "1x Tomato"], confirmed: false },
        { name: "3rd Option", ingredients: ["1x Ember Lily", "2x Peach"], confirmed: false },
        { name: "4th Option", ingredients: ["2x Banana", "2x Strawberry", "1x Pumpkin"], confirmed: false }
      ],
      Rare: [
        { name: "1st Option", ingredients: ["2x Corn", "2x Watermelon"], confirmed: false },
        { name: "2nd Option", ingredients: ["2x Corn", "2x Banana", "1x Watermelon"], confirmed: false },
        { name: "3rd Option", ingredients: ["1x Blueberry", "1x Grape", "1x Apple", "1x Corn"], confirmed: false }
      ],
      Legendary: [
        { name: "1st Option", ingredients: ["2x Kiwi", "2x Banana or Corn"], confirmed: false },
        { name: "2nd Option", ingredients: ["2x Banana", "1x Blood Banana", "1x Moon Melon", "1x Soft Sunshine"], confirmed: true }
      ],
      Mythical: [
        { name: "1st Option", ingredients: ["2x Sugar Apple", "2x Corn"], confirmed: false },
        { name: "2nd Option", ingredients: ["4x Sweet Type Crops", "4x Corn"], confirmed: false },
        { name: "3rd Option", ingredients: ["1x Sakura Bush", "1x Cacao", "1x Corn", "1x Giant Pinecone", "1x Spiked Mango"], confirmed: false },
        { name: "4th Option", ingredients: ["1x Banana or Corn", "1x Kiwi", "3x Bone Blossom"], confirmed: false }
      ],
      Divine: [
        { name: "1st Option", ingredients: ["1x Banana", "4x Sugar Apple"], confirmed: false },
        { name: "2nd Option", ingredients: ["1x Corn", "2x Elder Strawberry", "2x Sugar Apple"], confirmed: false },
        { name: "3rd Option", ingredients: ["1x Banana", "4x Elephant Ears"], confirmed: false },
        { name: "4th Option", ingredients: ["1x Corn", "1x Watermelon", "3x Bone Blossom"], confirmed: false },
        { name: "5th Option", ingredients: ["1x Corn", "4x Sugar Apple"], confirmed: false }
      ],
      Prismatic: [
        { name: "1st Option", ingredients: ["1x Banana", "4x Bone Blossom"], confirmed: true }
      ]
    }
  },
  Burger: {
    description: "The pinnacle of cooking achievement with prismatic properties",
    cookingTime: "12-15 minutes",
    difficulty: "Master",
    rarities: {
      Legendary: [
        { name: "1st Option", ingredients: ["1x Pepper", "1x Corn", "1x Tomato"], confirmed: false },
        { name: "2nd Option", ingredients: ["2x Bell Pepper", "1x Violet Corn"], confirmed: false }
      ],
      Mythical: [
        { name: "1st Option", ingredients: ["1x Pepper", "1x Corn", "1x Tomato", "1x Bone Blossom", "1x Beanstalk"], confirmed: false },
        { name: "2nd Option", ingredients: ["1x Pepper", "1x Corn", "1x Tomato", "2x Beanstalk"], confirmed: false },
        { name: "3rd Option", ingredients: ["1x Sugar Apple", "1x Pepper", "1x Corn", "1x Tomato", "1x Sugar Apple"], confirmed: false }
      ],
      Divine: [
        { name: "1st Option", ingredients: ["1x Corn", "1x Tomato", "3x Bone Blossom"], confirmed: true }
      ],
      Prismatic: [
        { name: "1st Option", ingredients: ["1x Banana", "1x Tomato", "3x Bone Blossom"], confirmed: true }
      ]
    }
  }
}

const cookingRewards = {
  Common: [
    "500 coins",
    "x1 Mutation Spray Burnt",
    "x1 Fork Fence",
    "x5 Watering Cans"
  ],
  Uncommon: [
    "x1 Food Crate",
    "x1 Reclaimer",
    "x3 Corn Seeds"
  ],
  Rare: [
    "x1 Gourmet Seed Pack",
    "x1 Artichoke Seed",
    "x1 Advanced Sprinkler"
  ],
  Legendary: [
    "x2 Small Toys",
    "x1 Gourmet Egg",
    "x1 Mutation Spray HoneyGlazed"
  ],
  Mythic: [
    "x1 Pretzel Cart",
    "x2 Gourmet Seed Packs",
    "x1 Medium Toy"
  ],
  Divine: [
    "x1 Mochi Mouse",
    "x2 Food Crates",
    "x1 Pet Mutation Shard Fried",
    "x2 Mutation Spray Fried"
  ],
  Prismatic: [
    "x1 Pancake Stack Cosmetic",
    "x1 Taco Fern Seed",
    "x3 Gourmet Eggs",
    "x4 Gourmet Seed Packs"
  ]
}

export default function EventTracker() {
  const { isAuthenticated, fetchCurrentEvent, fetchWeather } = useApi()
  const { showSuccess, showWarning, showInfo } = useNotificationHelpers()
  const [currentTime, setCurrentTime] = useState(Math.floor(Date.now() / 1000))
  
  // Recipe filtering state
  const [selectedRecipeType, setSelectedRecipeType] = useState('Salad')
  const [selectedRarity, setSelectedRarity] = useState('Normal')
  const [showOnlyConfirmed, setShowOnlyConfirmed] = useState(false)
  
  // Get available rarities for selected recipe type
  const availableRarities = useMemo(() => {
    const recipeData = cookingRecipes[selectedRecipeType as keyof typeof cookingRecipes]
    if (!recipeData) return []
    return Object.keys(recipeData.rarities)
  }, [selectedRecipeType])
  
  // Filter recipes based on selected type and rarity
  const filteredRecipes = useMemo(() => {
    const recipeData = cookingRecipes[selectedRecipeType as keyof typeof cookingRecipes]
    if (!recipeData) return []
    
    let recipes = recipeData.rarities[selectedRarity as keyof typeof recipeData.rarities] || []
    
    // Filter by confirmation status
    if (showOnlyConfirmed) {
      recipes = recipes.filter(recipe => recipe.confirmed)
    }
    
    return recipes
  }, [selectedRecipeType, selectedRarity, showOnlyConfirmed])

  // Real-time countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Math.floor(Date.now() / 1000))
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  const { data: eventData, isLoading, refetch } = useQuery({
    queryKey: ['currentEvent'],
    queryFn: fetchCurrentEvent,
    enabled: isAuthenticated,
    refetchInterval: 60000, // Refetch every minute
  })

  const { data: weatherData } = useQuery({
    queryKey: ['weather'],
    queryFn: fetchWeather,
    enabled: isAuthenticated,
    refetchInterval: 30000, // Refetch every 30 seconds
  })

  // Calculate event schedule
  const eventSchedule = eventData ? calculateEventSchedule(eventData, currentTime) : null

  // Event notifications
  useEffect(() => {
    if (!isAuthenticated || !eventSchedule) return

    // Check if event just started (within last 30 seconds)
    if (eventSchedule.isActive && eventSchedule.currentEvent) {
      const timeSinceStart = currentTime - eventSchedule.currentEvent.startTime
      if (timeSinceStart <= 30) {
        showSuccess(
          'Event Started!',
          `${eventSchedule.currentEvent.name} event is now active! Check the event tracker for strategies and rewards.`
        )
      }
    }

    // Check if event is about to end (within 1 hour)
    if (eventSchedule.isActive && eventSchedule.currentEvent?.timeUntilEnd) {
      if (eventSchedule.currentEvent.timeUntilEnd <= 3600 && eventSchedule.currentEvent.timeUntilEnd > 3540) {
        showWarning(
          'Event Ending Soon!',
          `${eventSchedule.currentEvent.name} event will end in 1 hour. Complete your objectives!`
        )
      }
    }

    // Check if event is starting soon (within 30 minutes)
    if (!eventSchedule.isActive && eventSchedule.timeUntilNext <= 1800 && eventSchedule.timeUntilNext > 1740) {
      showInfo(
        'Event Starting Soon!',
        `${eventData?.current?.name || 'New event'} will start in 30 minutes. Prepare your strategy!`
      )
    }
  }, [currentTime, eventSchedule, eventData, isAuthenticated, showSuccess, showWarning, showInfo])

  // Get event strategy
  const eventStrategy = useMemo(() => {
    if (!eventData?.current?.name) return eventStrategies.default
    
    // Handle Cooking Event specifically
    if (eventData.current.name.toLowerCase().includes('cooking') || 
        eventData.current.name.toLowerCase().includes('cook')) {
      return eventStrategies.CookingEvent
    }
    
    return eventStrategies[eventData.current.name as keyof typeof eventStrategies] || eventStrategies.default
  }, [eventData])

  // Check weather compatibility
  const weatherCompatibility = useMemo(() => {
    if (!weatherData?.weather || !eventStrategy) return null

    const activeWeather = weatherData.weather.filter(w => w.active)
    const compatibleWeather = activeWeather.filter(w => 
      eventStrategy.optimalWeather.includes(w.weather_name.toLowerCase())
    )

    return {
      compatible: compatibleWeather.length > 0,
      weatherTypes: compatibleWeather.map(w => w.weather_name),
      totalActive: activeWeather.length
    }
  }, [weatherData, eventStrategy])

  if (!isAuthenticated) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center min-h-[60vh]"
      >
        <div className="text-center max-w-md space-modern">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-r from-purple to-purple/80 flex items-center justify-center mb-4">
              <Calendar className="h-8 w-8 text-primary-foreground" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-foreground">Event Tracker</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            Please configure your API key using environment variables to view event data.
          </p>
          <div className="glass-card p-6 text-left mt-6">
            <p className="text-sm font-medium mb-3">Setup Instructions:</p>
            <ol className="text-xs text-muted-foreground space-y-2">
              <li>1. Create a <code className="bg-muted px-2 py-1 rounded-lg border border-border/30">.env</code> file in the project root</li>
              <li>2. Add <code className="bg-muted px-2 py-1 rounded-lg border border-border/30">VITE_API_KEY=your_api_key</code></li>
              <li>3. Restart the development server</li>
            </ol>
          </div>
        </div>
      </motion.div>
    )
  }

  const eventStats = [
    {
      name: 'Event Status',
      value: eventSchedule?.isActive ? 'Active' : 'Inactive',
      icon: eventSchedule?.isActive ? CheckCircle : Clock,
      color: eventSchedule?.isActive ? 'from-success to-success/80' : 'from-muted to-muted/80',
      bgColor: eventSchedule?.isActive ? 'bg-success/10' : 'bg-muted/10',
      borderColor: eventSchedule?.isActive ? 'border-success/30' : 'border-muted/30',
      description: eventSchedule?.isActive ? 'Event is currently running' : 'Event is not active',
      trend: eventSchedule?.isActive ? 'Active' : 'Waiting',
      trendUp: eventSchedule?.isActive || false
    },
    {
      name: 'Time Remaining',
      value: eventSchedule?.isActive && eventSchedule.currentEvent?.timeUntilEnd 
        ? formatTimeLeft(eventSchedule.currentEvent.endTime || 0, currentTime)
        : eventSchedule?.timeUntilNext && eventSchedule.timeUntilNext > 0 
        ? formatTimeLeft(currentTime + eventSchedule.timeUntilNext, currentTime)
        : 'N/A',
      icon: Clock,
      color: 'from-info to-info/80',
      bgColor: 'bg-info/10',
      borderColor: 'border-info/30',
      description: eventSchedule?.isActive ? 'Time left in event' : 'Time until event starts',
      trend: eventSchedule?.isActive ? 'Running' : 'Countdown',
      trendUp: true
    },
    {
      name: 'Weather Match',
      value: weatherCompatibility?.compatible ? 'Optimal' : 'Suboptimal',
      icon: weatherCompatibility?.compatible ? Sun : CloudRain,
      color: weatherCompatibility?.compatible ? 'from-warning to-warning/80' : 'from-muted to-muted/80',
      bgColor: weatherCompatibility?.compatible ? 'bg-warning/10' : 'bg-muted/10',
      borderColor: weatherCompatibility?.compatible ? 'border-warning/30' : 'border-muted/30',
      description: weatherCompatibility?.compatible ? 'Weather is optimal for event' : 'Weather could be better',
      trend: weatherCompatibility?.compatible ? 'Perfect' : 'Check',
      trendUp: weatherCompatibility?.compatible || false
    }
  ]

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-modern"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-modern-sm">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-3 rounded-2xl bg-gradient-to-r from-purple to-purple/80 shadow-lg">
              <Calendar className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground">
                Event Tracker
              </h1>
              <p className="text-muted-foreground leading-relaxed">
                Track current events, strategies, and optimal participation times
              </p>
            </div>
          </div>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => refetch()}
          disabled={isLoading}
          className="btn btn-outline btn-md"
        >
          <RefreshCw className={cn("h-4 w-4 mr-2", isLoading && "animate-spin")} />
          Refresh
        </motion.button>
      </div>

      {/* Current Event Status */}
      {eventData?.current && (
        <motion.div variants={itemVariants} className="gaming-card accent-purple">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-3 rounded-xl bg-gradient-to-r from-purple/20 to-purple/10 border border-purple/30">
                  <div className="w-6 h-6 text-purple">{eventStrategy.icon}</div>
                </div>
                <h3 className="card-title text-purple">{eventData.current.name}</h3>
              </div>
                             <div className="text-right">
                 <p className={cn("text-sm font-semibold", eventSchedule ? getEventStatusColor(eventSchedule) : 'text-muted-foreground')}>
                   {eventSchedule ? getEventStatusText(eventSchedule) : 'No Event Data'}
                 </p>
                 <p className="text-xs text-muted-foreground">
                   Start: {eventSchedule?.currentEvent?.startTime ? formatEventTime(eventSchedule.currentEvent.startTime) : 'N/A'}
                 </p>
               </div>
            </div>
            <div className="mt-2">
              <p className="text-sm text-muted-foreground">
                {eventStrategy.description}
              </p>
            </div>
          </div>
        </motion.div>
      )}









            {/* Cooking Event Recipes - Rearranged Layout */}
      <motion.div variants={itemVariants} className="gaming-card">
        <div className="card-header">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-xl bg-gradient-to-r from-orange/20 to-orange/10 border border-orange/30">
              <div className="w-6 h-6 text-orange">🍳</div>
            </div>
            <h3 className="card-title">Cooking Recipes</h3>
          </div>
        </div>
        <div className="card-content">
          {/* Top Controls Bar */}
          <div className="mb-6 flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Recipe Type Selector */}
            <div className="flex flex-wrap gap-2">
              {Object.keys(cookingRecipes).map((type) => (
                <button
                  key={type}
                  className={cn(
                    "px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200",
                    selectedRecipeType === type 
                      ? "bg-orange/20 text-orange shadow-sm border border-orange/30" 
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/30 border border-transparent"
                  )}
                  onClick={() => setSelectedRecipeType(type)}
                >
                  {type === 'HotDog' ? 'Hot Dog' : type === 'IceCream' ? 'Ice Cream' : type}
                </button>
              ))}
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowOnlyConfirmed(!showOnlyConfirmed)}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2",
                showOnlyConfirmed 
                  ? "bg-green/20 text-green border border-green/30" 
                  : "bg-muted/30 text-muted-foreground border border-border/30 hover:bg-muted/50"
              )}
            >
              <CheckCircle className="h-4 w-4" />
              <span>Confirmed Only</span>
            </button>
          </div>

                    {/* Main Content Area */}
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
            {/* Left Sidebar - Recipe Info & Rarity Tabs */}
            <div className="xl:col-span-1 space-y-4">
              {/* Recipe Info Card */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-orange/10 to-red/5 border border-orange/20">
                <div className="text-center mb-3">
                  <div className="w-12 h-12 mx-auto mb-2 rounded-lg bg-gradient-to-br from-orange/20 to-red/20 flex items-center justify-center">
                    <span className="text-xl">🍳</span>
                  </div>
                  <h4 className="font-bold text-base text-foreground">{selectedRecipeType}</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    {cookingRecipes[selectedRecipeType as keyof typeof cookingRecipes]?.description}
                  </p>
                </div>
                

              </div>

              {/* Rarity Navigation */}
              <div className="space-y-2">
                <h5 className="text-sm font-semibold text-muted-foreground">Rarity Tiers</h5>
              {availableRarities.map((rarity) => (
                <button
                  key={rarity}
                  className={cn(
                      "w-full px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center space-x-2",
                    selectedRarity === rarity 
                        ? "bg-gradient-to-r from-yellow/20 to-orange/20 text-yellow border border-yellow/30 shadow-sm" 
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/30 border border-transparent"
                  )}
                  onClick={() => setSelectedRarity(rarity)}
                >
                    <span className={cn(
                      "w-2 h-2 rounded-full",
                      rarity === "Normal" && "bg-gray-400",
                      rarity === "Uncommon" && "bg-green-400",
                      rarity === "Rare" && "bg-blue-400",
                      rarity === "Legendary" && "bg-purple-400",
                      rarity === "Mythical" && "bg-pink-400",
                      rarity === "Divine" && "bg-yellow-400",
                      rarity === "Prismatic" && "bg-gradient-to-r from-purple-400 to-pink-400",
                      "bg-gray-400" // Default fallback color
                    )}></span>
                    <span className="flex-1 text-left">{rarity}</span>
                </button>
              ))}
            </div>

              {/* Status Legend */}
              <div className="p-3 rounded-lg bg-muted/20 border border-border/30">
                <h5 className="font-semibold text-xs mb-2 text-center">Status</h5>
                <div className="space-y-1 text-xs">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-3 w-3 text-success" />
                    <span className="text-muted-foreground">Confirmed</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Unconfirmed</span>
                  </div>
                </div>
              </div>
          </div>

            {/* Main Content - Recipes Grid */}
            <div className="xl:col-span-4">
              {/* Results Summary */}
              <div className="flex items-center justify-between mb-4 text-sm text-muted-foreground">
                <span>
                  {filteredRecipes.length} recipe{filteredRecipes.length !== 1 ? 's' : ''} found
                  {showOnlyConfirmed && ' (confirmed only)'}
                </span>
                {showOnlyConfirmed && (
                  <button
                    onClick={() => setShowOnlyConfirmed(false)}
                    className="text-orange hover:text-orange/80 transition-colors text-xs"
                  >
                    Clear filter
                  </button>
                )}
              </div>

              {/* Recipe Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredRecipes.length > 0 ? (
                  filteredRecipes.map((recipe, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className={cn(
                        "p-4 rounded-xl border transition-all duration-200 hover:shadow-lg cursor-pointer",
                        recipe.confirmed 
                          ? "bg-gradient-to-br from-green/10 to-emerald/5 border-green/30" 
                          : "bg-gradient-to-br from-muted/20 to-muted/10 border-border/30"
                      )}
                    >
                      {/* Recipe Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <div className={cn(
                            "w-6 h-6 rounded-lg flex items-center justify-center",
                            recipe.confirmed 
                              ? "bg-green/20 text-green" 
                              : "bg-muted/30 text-muted-foreground"
                          )}>
                        {recipe.confirmed ? (
                              <CheckCircle className="h-3 w-3" />
                            ) : (
                              <Clock className="h-3 w-3" />
                            )}
                          </div>
                          <h4 className="font-semibold text-sm">
                            {recipe.name}
                          </h4>
                        </div>
                        <span className={cn(
                          "text-xs px-2 py-1 rounded-lg font-medium",
                          recipe.confirmed 
                            ? "bg-green/20 text-green" 
                            : "bg-muted/30 text-muted-foreground"
                        )}>
                          {recipe.confirmed ? "✓" : "?"}
                        </span>
                      </div>
                      
                      {/* Ingredients List */}
                      <div className="space-y-2">
                        <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Ingredients ({recipe.ingredients.length})
                        </h5>
                        <div className="space-y-1">
                        {recipe.ingredients.map((ingredient, ingIndex) => (
                          <div key={ingIndex} className="flex items-center space-x-2">
                              <div className="w-1 h-1 rounded-full bg-orange"></div>
                              <span className="text-xs text-foreground">{ingredient}</span>
                          </div>
                        ))}
                      </div>
                </div>


                    </motion.div>
                  ))
                ) : (
                  <div className="col-span-full text-center py-12">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-lg bg-muted/30 flex items-center justify-center">
                      <span className="text-xl">🍽️</span>
                    </div>
                    <p className="text-muted-foreground text-sm">No recipes found</p>
                    <p className="text-xs text-muted-foreground mt-1">Try different filters</p>
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
      </motion.div>

      


    </motion.div>
  )
} 